/* eslint-disable @typescript-eslint/no-unused-vars */
import classNames from 'classnames/bind';
import React, { ReactElement, ReactNode } from 'react';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import CompanyDealsWidget from '../../../components/widgets/Deals/CompanyDealsWidget';
import { useDocumentTitle } from '../../../hooks/common';
import useCollisionDetection from '../../../hooks/dragAndDrop/useCollisionDetection';
import useOnDragOverview from '../../../hooks/dragAndDrop/useOnDragOverview';
import { IssuerDto } from '../../../services/dtos/issuer/issuer.dto';
import CompanyOverviewWidget from '../../../components/widgets/CompanyOverview';
import CSViewCompanyWidget from '../../../components/widgets/CSView/CSViewCompanyWidget';
import PageComponentWidget from '../../../components/widgets/PageComponent';
import RecentlyPublished from '../../../components/widgets/RecentlyPublished';
import RiskProductsWidget from '../../../components/widgets/RiskProducts';
import SegmentsWidget from '../../../components/widgets/FinancialsSegments';
import { PageComponentsMap, PageComponentTagId } from '../../../models';
import { hideCSView } from '../Company.logic';
import { useRecentlyPublishedContext } from '../CompanyRecentlyPublishedContext';
import { CompanyWidgetId, Container, OVERVIEW_TAB_WIDGET_ID } from '../Company.constants';
import SortableElementsList from '../../../components/SortableElementsList';
import { DragOverlayProvider } from '../../../components/DragOverlay';
import useGetColumnview from '../../../hooks/company/useGetColumnView';
import styles from './CompanyOverviewTab.module.scss';
import CoreScoreWidget from '../../../components/widgets/CoreScore/CoreScoreWidget';
import { setScoreDataFormat } from '../../../utils/scoreUtils';

const cx = classNames.bind(styles);

export interface CompanyOverviewTabProps {
  company: IssuerDto;
  pageComponentsMap?: PageComponentsMap;
  hasDeals: boolean;
  hasFinancials: boolean;
  hasRiskProducts: boolean;
  showScoresWidget: boolean;
  selectedSectionsForPrint: string[];
}

const CompanyOverviewTab = ({
  company,
  pageComponentsMap = {},
  hasDeals,
  hasFinancials,
  hasRiskProducts,
  showScoresWidget,
  selectedSectionsForPrint,
}: CompanyOverviewTabProps): ReactElement => {
  const recentlyPublishedProps = useRecentlyPublishedContext();
  useDocumentTitle(`${company.displayName}: Overview`);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const columnView = useGetColumnview(company, pageComponentsMap);
  const { onDragStart, onDragEnd, onDragOver, draggableState } = useOnDragOverview(columnView);
  const { leftSideWidgets, middleWidgets, rightSideWidgets, activeContainer } = draggableState;
  const customCollisionDetection = useCollisionDetection();
  const scoreData = setScoreDataFormat(company.coreScores);
  const { hideSegments } = company;
  const showSegmentsWidget = hasFinancials && !hideSegments;
  const filterWidgets = (widgets, pageComponentsMap) =>
    widgets.filter(
      (widget) =>
        (pageComponentsMap[widget] && company.csCoverage) ||
        (OVERVIEW_TAB_WIDGET_ID[widget] &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.RISK_PRODUCT || hasRiskProducts) &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.DEALS || hasDeals) &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.CS_VIEW || !hideCSView(company)) &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.COMPANY_FIRST_SEGMENT || showSegmentsWidget) &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.COMPANY_SECOND_SEGMENT || showSegmentsWidget) &&
          (widget !== OVERVIEW_TAB_WIDGET_ID.CORE_SCORE || showScoresWidget))
    );

  const isNoPrint = (widgetId: CompanyWidgetId) => !selectedSectionsForPrint.includes(widgetId);

  const renderWidget = (widgetId: CompanyWidgetId, isDragged?: boolean) => {
    switch (widgetId) {
      case OVERVIEW_TAB_WIDGET_ID.COMPANY_OVERVIEW:
        return (
          <CompanyOverviewWidget
            key={widgetId}
            company={company}
            companyCoverageComponent={pageComponentsMap[PageComponentTagId.BUSINESS_DESCRIPTION]}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.COMPANY_OVERVIEW)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.COMPANY_FIRST_SEGMENT:
        return (
          <SegmentsWidget
            segmentGroupIndex={0}
            companyTagId={company.tagId}
            key={widgetId}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.COMPANY_FIRST_SEGMENT)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.COMPANY_SECOND_SEGMENT:
        return (
          <SegmentsWidget
            segmentGroupIndex={1}
            companyTagId={company.tagId}
            key={widgetId}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.COMPANY_SECOND_SEGMENT)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.RISK_PRODUCT:
        return (
          <RiskProductsWidget
            key={widgetId}
            companyTag={company.tagId}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.RISK_PRODUCT)}
            rpMessage
            isTeaser
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.RECENTLY_PUBLISHED:
        return (
          <RecentlyPublished
            key={widgetId}
            issuerTag={company.tagId}
            {...recentlyPublishedProps}
            showDragButton
            isDragged={isDragged}
            canAccess={company?.canAccess}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.RECENTLY_PUBLISHED)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.DEALS:
        return (
          <CompanyDealsWidget
            key={widgetId}
            companyTagId={company.tagId}
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.DEALS)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.CS_VIEW:
        return (
          <CSViewCompanyWidget
            key={widgetId}
            recommendation={company.latestRec}
            issuerTag={company.tagId}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.CS_VIEW)}
          />
        );
      case OVERVIEW_TAB_WIDGET_ID.CORE_SCORE:
        return (
          <CoreScoreWidget
            key={widgetId}
            showDragButton
            isDragged={isDragged}
            scoreNumber={scoreData.scoreNumber}
            outlookNumber={scoreData.outlookNumber}
            riskFactor={scoreData.riskFactor}
            nonRiskFactor={scoreData.nonRiskFactor}
            lastChanged={scoreData.lastChanged}
            lastReviewed={scoreData.lastReviewed}
            isScore={scoreData.isScore}
            noPrint={isNoPrint(OVERVIEW_TAB_WIDGET_ID.CORE_SCORE)}
          />
        );

      default:
        return (
          <PageComponentWidget
            key={widgetId}
            component={pageComponentsMap[widgetId]}
            showDragButton
            isDragged={isDragged}
            noPrint={isNoPrint(widgetId)}
          />
        );
    }
  };

  const getIsHighLighted = (containerId: string): boolean => {
    return (
      activeContainer === containerId ||
      (activeContainer === Container.LEFT && containerId === Container.RIGHT) ||
      (activeContainer === Container.RIGHT && containerId === Container.LEFT)
    );
  };

  const renderContainer = (
    container: Container,
    items: CompanyWidgetId[],
    classNames: string[] = []
  ): ReactNode | null => {
    if (!items || !items.length) {
      return null;
    }

    return (
      <div
        className={cx(...classNames, {
          'side-container': container !== Container.MIDDLE,
          'middle-container': container === Container.MIDDLE,
          'is-highlighted': getIsHighLighted(container),
        })}
        data-testid={container}
      >
        <SortableElementsList renderElement={renderWidget} items={items} id={container} />
      </div>
    );
  };

  const renderSections = (
    leftSideWidgets: CompanyWidgetId[],
    middleWidgets: CompanyWidgetId[],
    rightSideWidgets: CompanyWidgetId[]
  ) => {
    switch (columnView) {
      case 1:
        return renderContainer(
          Container.MIDDLE,
          filterWidgets([...leftSideWidgets, ...middleWidgets, ...rightSideWidgets], pageComponentsMap),
          ['tablet']
        );
      case 2:
        return (
          <>
            {renderContainer(Container.LEFT, filterWidgets(leftSideWidgets, pageComponentsMap))}
            {renderContainer(
              Container.MIDDLE,
              filterWidgets([...middleWidgets, ...rightSideWidgets], pageComponentsMap),
              ['tablet-landscape']
            )}
          </>
        );
      case 3:
        return (
          <>
            {renderContainer(Container.LEFT, filterWidgets(leftSideWidgets, pageComponentsMap))}
            {renderContainer(Container.MIDDLE, filterWidgets(middleWidgets, pageComponentsMap), ['desktop'])}
            {renderContainer(Container.RIGHT, filterWidgets(rightSideWidgets, pageComponentsMap))}
          </>
        );
      default:
        return undefined;
    }
  };

  return (
    <div className={cx('body')}>
      <DndContext
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={customCollisionDetection}
        sensors={sensors}
      >
        <DragOverlayProvider>{renderSections(leftSideWidgets, middleWidgets, rightSideWidgets)}</DragOverlayProvider>
      </DndContext>
    </div>
  );
};

export default CompanyOverviewTab;
